import { ErrorHandler, Injectable, inject } from '@angular/core';

import { ConfirmService } from '@shared/ui/overlay/confirm/confirm.service';

/**
 * Shows the user any error that nothing else caught.
 *
 * WHY: a `TypeError` thrown inside a click handler aborts whatever that handler
 * was doing and nothing else happens. Angular logs it to the console and the
 * screen carries on as if the button had done its job — which is how a crash in
 * the middle of Save looked exactly like a save that silently did nothing. The
 * only person who sees the console is whoever has it open.
 *
 * This is the non-HTTP half of the rule in standards §8: `apiErrorInterceptor`
 * catches failures that came back from a server, and this catches the ones we
 * caused ourselves. Between them, a failure cannot be invisible.
 *
 * The message is deliberately technical — a stack-level message is not something
 * a user can act on, but it IS something they can report, and reporting it beats
 * not knowing anything went wrong.
 */
@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private readonly confirm = inject(ConfirmService);

  handleError(error: unknown): void {
    // The console entry stays: it is the only thing carrying the stack.
    console.error(error);
    try {
      // One dialog at a time — a failure often cascades into several errors, and
      // the first is the one worth reading.
      if (this.confirm.isOpen()) return;
      void this.confirm.alert({
        title: 'Something went wrong',
        message: `${describe(error)}\n\nThe last thing you did may not have been saved. The full details are in the browser console.`,
      });
    } catch {
      // Never let reporting an error raise another one — that is how a single
      // failure turns into an unusable page.
    }
  }
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.message || error.name;
  return typeof error === 'string' ? error : 'An unexpected error occurred.';
}
